import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import { Platform, PrefKey, classNames } from '@standardnotes/snjs';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useApplication } from '@/Components/ApplicationProvider';
import { useCallback, useState } from 'react';
import { $createFileNode } from '../EncryptedFilePlugin/Nodes/FileUtils';
import { isIOS } from '@standardnotes/ui-services';
import Icon from '@/Components/Icon/Icon';
import Spinner from '@/Components/Spinner/Spinner';
import usePreference from '@/Hooks/usePreference';
import { getCSSValueFromAlignment, ImageAlignmentOptions } from '@/Components/FilePreview/ImageAlignmentOptions';
import { getOverflows } from '@/Components/Popover/Utils/Collisions';
import { c } from 'ttag';
const InlineFileComponent = ({ className, src, mimeType, fileName, format, setFormat, node, nodeKey }) => {
    const application = useApplication();
    const [editor] = useLexicalComposerContext();
    const [isSaving, setIsSaving] = useState(false);
    const saveToFilesAndReplaceNode = useCallback(async () => {
        setIsSaving(true);
        try {
            const blob = await fetch(src).then((response) => response.blob());
            const file = new File([blob], fileName || application.generateUUID(), { type: mimeType });
            const { filesController, linkingController } = application;
            const uploadedFile = await filesController.uploadNewFile(file, { showToast: false });
            if (!uploadedFile) {
                return;
            }
            editor.update(() => {
                const fileNode = $createFileNode(uploadedFile.uuid);
                node.replace(fileNode);
            });
            void linkingController.linkItemToSelectedItem(uploadedFile);
        }
        catch (error) {
            console.error(error);
        }
        finally {
            setIsSaving(false);
        }
    }, [application, editor, fileName, mimeType, node, src]);
    const isPDF = mimeType === 'application/pdf';
    const defaultSuperImageAlignment = usePreference(PrefKey.SuperNoteImageAlignment);
    const finalAlignment = format || defaultSuperImageAlignment;
    const alignItems = getCSSValueFromAlignment(finalAlignment);
    const changeAlignment = useCallback((format) => {
        editor.update(() => {
            setFormat(format);
        });
    }, [editor, setFormat]);
    return (_jsxs(BlockWithAlignableContents, { className: className, format: format, nodeKey: nodeKey, children: [mimeType.startsWith('image') ? (_jsxs("div", { className: "group relative flex min-h-[2rem] flex-col gap-2.5", style: { alignItems }, onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }, children: [_jsx("img", { alt: fileName, src: src }), _jsx("div", { className: "invisible absolute bottom-full left-1/2 z-10 w-max -translate-x-1/2 px-1 pb-1 focus-within:visible group-hover:visible [.embedBlockFocused_&]:visible", ref: (popover) => {
                            const editorRoot = editor.getRootElement();
                            if (!popover || !editorRoot) {
                                return;
                            }
                            const editorRootRect = editorRoot.getBoundingClientRect();
                            const popoverRect = popover.getBoundingClientRect();
                            const overflows = getOverflows(popoverRect, editorRootRect);
                            if (overflows.top > 0) {
                                popover.style.setProperty('--tw-translate-y', `${overflows.top}px`);
                            }
                        }, children: _jsx("div", { className: "flex gap-1 rounded border border-border bg-default px-1 py-0.5", children: _jsx(ImageAlignmentOptions, { alignment: finalAlignment, changeAlignment: changeAlignment }) }) })] })) : mimeType.startsWith('video') ? (_jsx("video", { className: "h-full w-full", controls: true, autoPlay: true, children: _jsx("source", { src: src, type: mimeType }) })) : mimeType.startsWith('audio') ? (_jsx("div", { className: "flex h-full w-full items-center justify-center", children: _jsx("audio", { controls: true, children: _jsx("source", { src: src, type: mimeType }) }) })) : (_jsx("object", { className: classNames('h-full w-full', isPDF && 'min-h-[65vh]'), data: isPDF ? src + '#view=FitV' : src })), _jsx("button", { className: classNames('mx-auto mt-2 flex items-center gap-2.5 rounded border border-border bg-default px-2.5 py-1.5', !isSaving && 'hover:bg-info hover:text-info-contrast'), onClick: () => {
                    const isIOSPlatform = application.platform === Platform.Ios || isIOS();
                    if (isIOSPlatform && document.activeElement) {
                        ;
                        document.activeElement.blur();
                    }
                    saveToFilesAndReplaceNode().catch(console.error);
                }, disabled: isSaving, children: isSaving ? (_jsxs(_Fragment, { children: [_jsx(Spinner, { className: "h-4 w-4" }), c('B4.Notes.EditingUI.Status').t `Saving...`] })) : (_jsxs(_Fragment, { children: [_jsx(Icon, { type: "download" }), c('B4.Notes.EditingUI.Action').t `Save to Files`] })) })] }));
};
export default InlineFileComponent;
